import { destinations } from '@/mocks/destinations';
import { wikiArticles, type WikiArticle } from '@/mocks/wikiArticles';
import { wikiArticlesEn } from '@/mocks/wikiArticles.en';
import { wikiArticlesZhTw } from '@/mocks/wikiArticles.zhTW';
import { wikiPolarArticles } from '@/mocks/wikiPolarArticles';
import { wikiPolarArticlesEn } from '@/mocks/wikiPolarArticles.en';
import { wikiPolarArticlesZhTw } from '@/mocks/wikiPolarArticles.zhTW';
import { wikiBrandArticles } from '@/mocks/wikiBrandArticles';
import { wikiBrandArticlesEn } from '@/mocks/wikiBrandArticles.en';
import { wikiBrandArticlesZhTw } from '@/mocks/wikiBrandArticles.zhTW';
import { themes } from '@/mocks/themes';
import { getLangPrefix, stripLangPrefix } from '@/utils/locale';
import { SITE_URL, publicUrl } from './url';
import { LAST_MODIFIED_BY_PATH } from './lastmod';

export { SITE_URL };

export interface Hreflang {
  lang: string;
  url: string;
}

export interface SeoMeta {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: string;
  path: string;
  url: string;
  robots?: string;
  canonicalUrl?: string;
  hreflangs?: Hreflang[];
  htmlLang?: string;
  ogLocale?: string;
}

interface StaticMeta {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: string;
}

function truncate(text: string, max = 160): string {
  if (!text) return '';
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + '…';
}

const staticRoutes: Record<string, StaticMeta> = {
  '/': {
    title: '自由旷野旅行官网｜非洲Safari·南北极野奢定制旅行',
    description:
      '自由旷野旅行（Free Wilderness Travel）官方网站。品牌成立于2022年，由成都自由旷野国际旅行社有限公司运营，专注非洲Safari、南北极及自然类野奢定制旅行。创始人及核心团队拥有13年目的地经验，旅行社业务经营许可证 L-SC-100448。',
  },
  '/about': {
    title: '关于我们 - 自由旷野旅行',
    description:
      '自由旷野旅行品牌故事与创始团队：成立于2022年，由成都自由旷野国际旅行社有限公司运营，专注非洲Safari、南北极及自然类野奢定制旅行，创始人及核心团队深耕非洲与极地目的地13年。',
  },
  '/contact': {
    title: '联系我们 - 自由旷野旅行',
    description:
      '联系自由旷野旅行专属旅行顾问，24小时在线服务，一对一解答定制疑问，成都总部欢迎预约到访，极速响应您的旅行需求。',
  },
  '/custom': {
    title: '定制旅行 - 自由旷野旅行',
    description:
      '自由旷野旅行高端定制：告诉我们您的目的地与旅行偏好，专属顾问一对一量身设计行程，覆盖非洲Safari、南北极、北欧等目的地。',
  },
  '/custom-service': {
    title: '私人定制服务 - 自由旷野旅行',
    description:
      '自由旷野旅行私人定制服务：一对一专属顾问，完全个性化行程设计，顶级资源直连，从家庭度假到环球探险全程无忧保障。',
  },
  '/enterprise': {
    title: '企业高端定制旅行 - 自由旷野旅行',
    description:
      '企业团建、奖励旅游、商务考察定制：覆盖非洲Safari、极地探险、野奢海岛，提供正规发票与合同，长期企业客户服务经验。',
  },
  '/africa': {
    title: '非洲Safari定制旅行 - 自由旷野旅行',
    description:
      '非洲Safari定制旅行：博茨瓦纳、坦桑尼亚、肯尼亚、卢旺达核心目的地，动物大迁徙、水陆游猎、山地大猩猩追踪，入住顶级野奢营地。',
  },
  '/polar': {
    title: '南北极探险定制旅行 - 自由旷野旅行',
    description:
      '南北极探险定制：南极大陆登陆、北极斯瓦尔巴追寻北极熊、格陵兰岛极光，合作顶级极地邮轮，深耕极地旅行定制。',
  },
  '/polar-finder': {
    title: '极地航次查询｜南极与北极探险邮轮筛选 - 自由旷野旅行',
    description:
      '汇总合作船司官网公开航次，可按出发时间、预算和目的区域快速筛选；结果可直达船司官网。价格与余位以船司官网为准。',
  },
  '/destinations': {
    title: '全球高端旅行目的地 - 自由旷野旅行',
    description:
      '自由旷野旅行与全球60+国家和地区的高端酒店、Safari营地及旅行资源方保持长期合作：南极、北极、博茨瓦纳、坦桑尼亚、肯尼亚、卢旺达、北欧、野奢海岛等，专业定制实地勘察。',
  },
  '/themes': {
    title: '六大主题私人定制旅行 - 自由旷野旅行',
    description:
      '自由旷野旅行六大定制主题：极地探险、非洲Safari、野奢海岛、奢华邮轮、北欧秘境、生态研学，一对一量身设计专属行程。',
  },
  '/explore': {
    title: '旅行灵感 - 自由旷野旅行',
    description:
      '获取旅行灵感：非洲Safari体验记、南极探险日记、野奢营地实拍，从旅行风格或目的地出发，探索属于您的定制灵感。',
  },
  '/customer-stories': {
    title: '真实客户反馈与非洲旅行实拍｜自由旷野',
    description:
      '自由旷野匿名客户回访与旅途实拍，记录坦桑尼亚、博茨瓦纳、卢旺达等行程中的酒店、向导、Safari、家庭同行与多国深度旅行体验。',
    keywords: '非洲旅行,客户反馈,坦桑尼亚,博茨瓦纳,卢旺达,Safari,野生动物',
  },
  '/academy': {
    title: '自由旷野学院 - 栏目迁移说明 - 自由旷野旅行',
    description:
      '原「自由旷野学院」栏目已整合并入「旷野百科」，非洲Safari攻略、极地探险指南、签证办理、营地选择等旅行知识现已统一收录，欢迎前往旷野百科查阅。',
  },
  '/wiki': {
    title: '旷野百科 - 自由旷野旅行',
    description:
      '自由旷野旅行旷野百科：专业目的地知识百科，涵盖非洲Safari攻略、极地探险指南、签证办理、营地选择等深度旅行知识。',
    keywords: '旷野百科,非洲百科,非洲Safari攻略,极地探险指南,签证办理,自由旷野旅行',
  },
  '/wiki/africa': {
    title: '非洲百科 - 旷野百科 - 自由旷野旅行',
    description:
      '自由旷野旅行非洲百科：博茨瓦纳、坦桑尼亚、肯尼亚等非洲核心目的地深度攻略，Safari知识、签证指南、营地住宿选择。',
    keywords: '非洲百科,非洲旅行攻略,博茨瓦纳,坦桑尼亚,肯尼亚,Safari,签证,营地住宿',
  },
  '/en/wiki/africa': {
    title: 'Africa Encyclopedia - Wilderness Wiki - Free Wilderness Travel',
    description:
      'Free Wilderness Africa Encyclopedia: in-depth guides to Botswana, Tanzania and Kenya, covering safari knowledge, visa guides, and camp and lodge selection.',
    keywords: 'Africa encyclopedia, Africa travel guide, Botswana, Tanzania, Kenya, Safari, visa, camps',
  },
  '/zh-TW/wiki/africa': {
    title: '非洲百科 - 曠野百科 - 自由曠野旅行',
    description:
      '自由曠野旅行非洲百科：博茨瓦納、坦桑尼亞、肯亞等非洲核心目的地深度攻略，Safari 知識、簽證指南、營地住宿選擇。',
    keywords: '非洲百科,非洲旅行攻略,博茨瓦納,坦桑尼亞,肯亞,Safari,簽證,營地住宿',
  },
  '/wiki/polar': {
    title: '极地百科 - 旷野百科 - 自由旷野旅行',
    description:
      '自由旷野旅行极地百科：南极旅行季节、南极邮轮航线选择、德雷克海峡、北极目的地等极地探险深度攻略。',
    keywords: '极地百科,南极旅行,南极邮轮,南极航线,北极探险,极地攻略,自由旷野旅行',
  },
  '/wiki/brand': {
    title: '品牌百科 - 旷野百科 - 自由旷野旅行',
    description:
      '自由旷野旅行品牌百科：创始人实地笔记、品牌故事与人物记录，展现十余年非洲与极地目的地深耕背后的旅行哲学。',
    keywords: '自由旷野旅行,品牌故事,创始人,李志强,实地笔记,非洲Safari,南极,卢旺达大猩猩',
  },
  '/en/wiki/polar': {
    title: 'Polar Encyclopedia - Wilderness Wiki - Free Wilderness Travel',
    description:
      'Free Wilderness Polar Encyclopedia: in-depth guides to Antarctic travel seasons, cruise routes, the Drake Passage, and Arctic destinations.',
    keywords: 'Polar encyclopedia, Antarctica travel, Antarctic cruise, Antarctic routes, Arctic expedition, polar guide, Free Wilderness',
  },
  '/zh-TW/wiki/polar': {
    title: '極地百科 - 曠野百科 - 自由曠野旅行',
    description:
      '自由曠野旅行極地百科：南極旅行季節、南極郵輪航線選擇、德雷克海峽、北極目的地等極地探險深度攻略。',
    keywords: '極地百科,南極旅行,南極郵輪,南極航線,北極探險,極地攻略,自由曠野旅行',
  },
  '/en/wiki/brand': {
    title: 'Brand Stories - Wilderness Wiki - Free Wilderness Travel',
    description:
      'Free Wilderness Brand Stories: founder field notes, brand story, and people profiles revealing the travel philosophy behind over a decade of Africa and polar destination expertise.',
    keywords: 'Free Wilderness, brand story, founder, Li Zhiqiang, field notes, African safari, Antarctica, Rwanda gorilla',
  },
  '/zh-TW/wiki/brand': {
    title: '品牌百科 - 曠野百科 - 自由曠野旅行',
    description:
      '自由曠野旅行品牌百科：創始人實地筆記、品牌故事與人物記錄，展現十餘年非洲與極地目的地深耕背後的旅行哲學。',
    keywords: '自由曠野旅行,品牌故事,創始人,李志強,實地筆記,非洲Safari,南極,盧旺達大猩猩',
  },
  '/safari-packages/tanzania/2027-all-asilia-northern-circuit': {
    title: '2027坦桑尼亚All Asilia野奢Safari 8天7晚｜自由旷野',
    description:
      '2027坦桑尼亚北部8天7晚Safari参考行程，从塔兰吉雷Oliver\'s Camp、恩戈罗恩戈罗The Highlands前往塞伦盖蒂或恩杜图，可选Sayari、Namiri Plains或按季节迁营的Olakira，包含内陆航班、营地住宿、共享游猎与分季参考价格。',
    keywords: 'All Asilia,坦桑尼亚Safari,塔兰吉雷,恩戈罗恩戈罗,塞伦盖蒂,Olakira,Sayari,Namiri Plains,8天7晚',
  },
  '/safari-packages/tanzania/2027-lemala-safari': {
    title: '2027坦桑尼亚Lemala野奢Safari 8天7晚｜自由旷野',
    description:
      '2027坦桑尼亚8天7晚Lemala野奢Safari参考行程，串联塔兰吉雷、恩戈罗恩戈罗与塞伦盖蒂，可选中部Nanyukie或北部Kuria Hills，清楚展示逐日路线、内陆航班、共享游猎、分季参考价格与费用边界。',
    keywords: 'Lemala,坦桑尼亚Safari,塔兰吉雷,恩戈罗恩戈罗,塞伦盖蒂,Nanyukie,Kuria Hills,8天7晚',
  },
  '/safari-packages/east-africa/2026-andbeyond-safari': {
    title: '2026东非野奢Safari 8天7晚｜肯尼亚与坦桑尼亚｜自由旷野',
    description:
      '2026东非8天7晚Safari参考方案，以andBeyond营地为核心，可选择坦桑尼亚北部、肯尼亚与坦桑尼亚联游、迁徙追踪或家庭游猎路线，清楚展示目的地、营地组合、内陆交通、活动和费用边界。',
    keywords: 'andBeyond,东非Safari,肯尼亚,坦桑尼亚,8天7晚,迁徙追踪,家庭游猎,野奢营地',
  },
};

function getAfricaArticle(slug: string, lang: 'en' | 'zh-TW' | null): WikiArticle | undefined {
  if (lang === 'en') return wikiArticlesEn.find((a) => a.slug === slug);
  if (lang === 'zh-TW') return wikiArticlesZhTw.find((a) => a.slug === slug);
  return wikiArticles.find((a) => a.slug === slug);
}

/** 非洲百科文章的三种语言版本 + x-default 互链。 */
function buildAfricaArticleHreflangs(slug: string): Hreflang[] {
  return [
    { lang: 'zh', url: publicUrl(`/wiki/africa/${slug}`) },
    { lang: 'zh-TW', url: publicUrl(`/zh-TW/wiki/africa/${slug}`) },
    { lang: 'en', url: publicUrl(`/en/wiki/africa/${slug}`) },
    { lang: 'x-default', url: publicUrl(`/wiki/africa/${slug}`) },
  ];
}

/** 非洲百科列表页的三种语言版本 + x-default 互链。 */
function buildAfricaListHreflangs(): Hreflang[] {
  return [
    { lang: 'zh', url: publicUrl('/wiki/africa') },
    { lang: 'zh-TW', url: publicUrl('/zh-TW/wiki/africa') },
    { lang: 'en', url: publicUrl('/en/wiki/africa') },
    { lang: 'x-default', url: publicUrl('/wiki/africa') },
  ];
}

function getBrandArticle(slug: string, lang: 'en' | 'zh-TW' | null) {
  if (lang === 'en') return wikiBrandArticlesEn.find((a) => a.slug === slug);
  if (lang === 'zh-TW') return wikiBrandArticlesZhTw.find((a) => a.slug === slug);
  return wikiBrandArticles.find((a) => a.slug === slug);
}

function getPolarArticle(slug: string, lang: 'en' | 'zh-TW' | null) {
  if (lang === 'en') return wikiPolarArticlesEn.find((a) => a.slug === slug);
  if (lang === 'zh-TW') return wikiPolarArticlesZhTw.find((a) => a.slug === slug);
  return wikiPolarArticles.find((a) => a.slug === slug);
}

function buildBrandArticleHreflangs(slug: string): Hreflang[] {
  return [
    { lang: 'zh', url: publicUrl(`/wiki/brand/${slug}`) },
    { lang: 'zh-TW', url: publicUrl(`/zh-TW/wiki/brand/${slug}`) },
    { lang: 'en', url: publicUrl(`/en/wiki/brand/${slug}`) },
    { lang: 'x-default', url: publicUrl(`/wiki/brand/${slug}`) },
  ];
}

function buildPolarArticleHreflangs(slug: string): Hreflang[] {
  return [
    { lang: 'zh', url: publicUrl(`/wiki/polar/${slug}`) },
    { lang: 'zh-TW', url: publicUrl(`/zh-TW/wiki/polar/${slug}`) },
    { lang: 'en', url: publicUrl(`/en/wiki/polar/${slug}`) },
    { lang: 'x-default', url: publicUrl(`/wiki/polar/${slug}`) },
  ];
}

function buildBrandListHreflangs(): Hreflang[] {
  return [
    { lang: 'zh', url: publicUrl('/wiki/brand') },
    { lang: 'zh-TW', url: publicUrl('/zh-TW/wiki/brand') },
    { lang: 'en', url: publicUrl('/en/wiki/brand') },
    { lang: 'x-default', url: publicUrl('/wiki/brand') },
  ];
}

function buildPolarListHreflangs(): Hreflang[] {
  return [
    { lang: 'zh', url: publicUrl('/wiki/polar') },
    { lang: 'zh-TW', url: publicUrl('/zh-TW/wiki/polar') },
    { lang: 'en', url: publicUrl('/en/wiki/polar') },
    { lang: 'x-default', url: publicUrl('/wiki/polar') },
  ];
}

/** 列表页 meta：带语言前缀时优先取对应语言的 meta（英文/繁体已在 staticRoutes 中定义）。 */
function resolveStaticMeta(cleanPath: string, langPrefix: 'en' | 'zh-TW' | null): StaticMeta | undefined {
  const base = staticRoutes[cleanPath];
  if (!base) return undefined;
  if (!langPrefix) return base;
  return staticRoutes[`/${langPrefix}${cleanPath}`] || base;
}

function notFoundMeta(path: string): SeoMeta {
  return {
    title: '页面未找到 - 自由旷野旅行',
    description: '您访问的页面不存在、已移动或已删除。',
    path,
    url: publicUrl(path),
    robots: 'noindex,follow',
  };
}

function computeSeoMeta(pathname: string): SeoMeta {
  let path = pathname || '/';
  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }

  const langPrefix = getLangPrefix(path);
  const cleanPath = stripLangPrefix(path);

  // 带语言前缀的 URL 仅对三套百科（非洲/极地/品牌）有效；
  // 其余带前缀路径（如 /en/about、/zh-TW/custom、/en/）不属于公开路由，返回 404。
  const isLocalizedWikiPath =
    cleanPath === '/wiki/africa' ||
    cleanPath.startsWith('/wiki/africa/') ||
    cleanPath === '/wiki/polar' ||
    cleanPath.startsWith('/wiki/polar/') ||
    cleanPath === '/wiki/brand' ||
    cleanPath.startsWith('/wiki/brand/');
  if (langPrefix && !isLocalizedWikiPath) {
    return notFoundMeta(path);
  }

  if (cleanPath.startsWith('/destinations/')) {
    const id = cleanPath.slice('/destinations/'.length);
    const dest = destinations.find((d) => d.id === id);
    if (dest) {
      return {
        title: `${dest.name}旅行攻略 - 自由旷野旅行`,
        description: truncate(dest.description),
        path,
        url: publicUrl(path),
      };
    }
  }

  if (cleanPath.startsWith('/wiki/africa/')) {
    const slug = cleanPath.slice('/wiki/africa/'.length);
    const article = getAfricaArticle(slug, langPrefix);
    if (article) {
      return {
        title: article.metaTitle,
        description: article.metaDescription,
        keywords: article.metaKeywords,
        image: article.image,
        type: 'article',
        path,
        url: publicUrl(path),
        hreflangs: buildAfricaArticleHreflangs(slug),
      };
    }
  }

  if (cleanPath.startsWith('/wiki/polar/')) {
    const slug = cleanPath.slice('/wiki/polar/'.length);
    const article = getPolarArticle(slug, langPrefix);
    if (article) {
      return {
        title: article.metaTitle,
        description: article.metaDescription,
        keywords: article.metaKeywords,
        image: article.image,
        type: 'article',
        path,
        url: publicUrl(path),
        hreflangs: buildPolarArticleHreflangs(slug),
      };
    }
  }

  if (cleanPath.startsWith('/wiki/brand/')) {
    const slug = cleanPath.slice('/wiki/brand/'.length);
    const article = getBrandArticle(slug, langPrefix);
    if (article) {
      return {
        title: article.metaTitle,
        description: article.metaDescription,
        keywords: article.metaKeywords,
        image: article.image,
        type: 'article',
        path,
        url: publicUrl(path),
        hreflangs: buildBrandArticleHreflangs(slug),
      };
    }
  }

  if (cleanPath.startsWith('/themes/')) {
    const id = cleanPath.slice('/themes/'.length);
    const theme = themes.find((t) => t.id === id);
    if (theme) {
      return {
        title: `${theme.name}定制旅行 - 自由旷野旅行`,
        description: truncate(theme.description),
        path,
        url: publicUrl(path),
      };
    }
  }

  const meta = resolveStaticMeta(cleanPath, langPrefix);
  if (meta) {
    const result: SeoMeta = {
      ...meta,
      path,
      url: publicUrl(path),
    };
    // 旧学院页已在源码层面设为 noindex，并将 canonical 指向旷野百科。
    if (cleanPath === '/academy') {
      result.robots = 'noindex,follow';
      result.canonicalUrl = publicUrl('/wiki');
    }
    // 三个百科列表页：三种语言互链（cleanPath 已去前缀，三种语言版本共用同一判断）。
    if (cleanPath === '/wiki/africa') {
      result.hreflangs = buildAfricaListHreflangs();
    }
    if (cleanPath === '/wiki/polar') {
      result.hreflangs = buildPolarListHreflangs();
    }
    if (cleanPath === '/wiki/brand') {
      result.hreflangs = buildBrandListHreflangs();
    }
    return result;
  }

  // /admin 为站内管理页（不预渲染、不进 sitemap），不做 404 判定，仅给出基础元数据。
  if (path === '/admin') {
    return {
      title: '自由旷野旅行',
      description: '自由旷野旅行官方网站。',
      path,
      url: publicUrl(path),
      robots: 'noindex,follow',
    };
  }

  // 未知路径 / 不存在的文章、目的地、主题 → 输出独立 404 元数据（noindex），不回首页。
  return notFoundMeta(path);
}

export function getSeoMeta(pathname: string): SeoMeta {
  const meta = computeSeoMeta(pathname);
  const langPrefix = getLangPrefix(pathname);
  return {
    ...meta,
    htmlLang: langPrefix === 'en' ? 'en' : langPrefix === 'zh-TW' ? 'zh-TW' : 'zh-CN',
    ogLocale: langPrefix === 'en' ? 'en_US' : langPrefix === 'zh-TW' ? 'zh_TW' : 'zh_CN',
  };
}

export function getAllPrerenderRoutes(): string[] {
  const routes: string[] = Object.keys(staticRoutes);
  destinations.forEach((d) => routes.push(`/destinations/${d.id}`));
  themes.forEach((t) => routes.push(`/themes/${t.id}`));
  wikiArticles.forEach((a) => {
    routes.push(`/wiki/africa/${a.slug}`);
    routes.push(`/en/wiki/africa/${a.slug}`);
    routes.push(`/zh-TW/wiki/africa/${a.slug}`);
  });
  wikiPolarArticles.forEach((a) => {
    routes.push(`/wiki/polar/${a.slug}`);
    routes.push(`/en/wiki/polar/${a.slug}`);
    routes.push(`/zh-TW/wiki/polar/${a.slug}`);
  });
  wikiBrandArticles.forEach((a) => {
    routes.push(`/wiki/brand/${a.slug}`);
    routes.push(`/en/wiki/brand/${a.slug}`);
    routes.push(`/zh-TW/wiki/brand/${a.slug}`);
  });
  return routes;
}

export interface SitemapEntry {
  path: string;
  priority: number;
  changefreq: string;
  lastmod?: string;
}

function getSitemapPriority(path: string): number {
  if (path === '/') return 1.0;
  if (path === '/custom') return 0.9;
  if (path.startsWith('/destinations/') || path.startsWith('/themes/')) return 0.7;
  return 0.8;
}

function getSitemapChangefreq(path: string): string {
  if (path.startsWith('/destinations/') || path.startsWith('/themes/')) return 'monthly';
  if (['/about', '/contact', '/enterprise', '/custom-service'].includes(path)) return 'monthly';
  return 'weekly';
}

// 过渡/迁移页：仍需生成静态 HTML 供直接访问，但不进入 Sitemap。
const SITEMAP_EXCLUDED_PATHS = new Set(['/academy']);

// sitemap 与预渲染路由共用同一份真实路由来源，避免两者漂移；
// 仅对明确排除的路径做最小过滤，不重构预渲染机制。
// 说明：本站当前没有可靠的内容「修改时间」数据（文章 date 仅为发布日期），
// 因此不输出 lastmod，避免把发布日期冒充为修改日期或用全站固定日期敷衍。

export function getSitemapEntries(): SitemapEntry[] {
  return getAllPrerenderRoutes()
    .filter((path) => !SITEMAP_EXCLUDED_PATHS.has(path))
    .map((path) => ({
      path,
      priority: getSitemapPriority(path),
      changefreq: getSitemapChangefreq(path),
      // lastmod 仅在登记了「可追溯的真实内容变更时间」时输出（见 src/seo/lastmod.ts）；
      // 未知时保持 undefined，buildSitemap 会省略该节点，避免用构建日/发布日期冒充修改日。
      lastmod: LAST_MODIFIED_BY_PATH[path],
    }));
}